import { zodResolver } from "@hookform/resolvers/zod";
import { Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const ITEM_STATUSES = ["draft", "active", "archived"] as const;

const itemEditSchema = z.object({
  name: z.string().min(1, "Name is required").max(500, "Name is too long"),
  description: z.string().max(2000, "Description is too long").optional(),
  status: z.enum(ITEM_STATUSES),
});

type ItemEditValues = z.infer<typeof itemEditSchema>;

interface ItemDetail {
  name: string;
  description: string | null;
  status: string;
}

interface ItemEditFormProps {
  item: ItemDetail;
  onSave: (updates: ItemEditValues) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function ItemEditForm({ item, onSave, onCancel, isSaving }: ItemEditFormProps) {
  const form = useForm<ItemEditValues>({
    resolver: zodResolver(itemEditSchema),
    defaultValues: {
      name: item.name,
      description: item.description ?? "",
      status: item.status as ItemEditValues["status"],
    },
  });

  function handleSubmit(values: ItemEditValues) {
    onSave({
      ...values,
      description: values.description || undefined,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Item name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Add a description..." className="min-h-[100px]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
